import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const KursfarbenPage = () => {
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchColors();
  }, []);

  const fetchColors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/api/admin/kurs-colors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setColors(response.data.colors || []);
    } catch (error) {
      setColors([
        { name: "Junior", color: "#fce7f3" },
        { name: "Bronze", color: "#dcfce7" },
        { name: "Silber", color: "#fed7aa" },
        { name: "Gold", color: "#dbeafe" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/api/admin/kurs-colors`, 
        { id: "kurs_color_config", colors },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kursfarben gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (index, field, value) => {
    const newColors = [...colors];
    newColors[index] = { ...newColors[index], [field]: value };
    setColors(newColors);
  };

  const handleAddColor = () => {
    setColors([...colors, { name: '', color: '#f3f4f6' }]);
  };

  const handleRemoveColor = (index) => {
    setColors(colors.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kursfarben</h1>
          <p className="text-sm text-gray-600">Farben für verschiedene Kurstypen definieren</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Speichern
        </Button>
      </div>

      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Farbzuordnungen</CardTitle>
          <CardDescription className="text-xs">
            Kurse werden eingefärbt, wenn der Name den Begriff enthält
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {colors.map((colorConfig, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="flex-1">
                <Input
                  value={colorConfig.name}
                  onChange={(e) => handleColorChange(index, 'name', e.target.value)}
                  placeholder="z.B. Junior, Bronze"
                  className="h-9"
                />
              </div>
              <input
                type="color"
                value={colorConfig.color}
                onChange={(e) => handleColorChange(index, 'color', e.target.value)}
                className="h-9 w-12 rounded border cursor-pointer"
              />
              <Input
                value={colorConfig.color}
                onChange={(e) => handleColorChange(index, 'color', e.target.value)}
                className="w-24 h-9 font-mono text-xs"
              />
              <div 
                className="w-16 h-9 rounded border flex items-center justify-center text-[10px]"
                style={{ backgroundColor: colorConfig.color }}
              >
                Vorschau
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveColor(index)}
                className="h-9 w-9 p-0 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={handleAddColor} className="w-full">
            <Plus className="h-4 w-4 mr-1" />
            Farbe hinzufügen
          </Button>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">Vorschau</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {colors.filter(c => c.name).map((colorConfig, index) => (
              <div
                key={index}
                className="p-2 rounded border-l-4 text-xs font-medium"
                style={{ 
                  borderLeftColor: colorConfig.color,
                  backgroundColor: `${colorConfig.color}30`
                }}
              >
                {colorConfig.name} 1
              </div>
            ))}
            <div className="p-2 rounded border-l-4 border-gray-300 bg-gray-50 text-xs text-gray-500">
              Sonstige
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default KursfarbenPage;
